import { useState } from "react"
import { Chip } from "@/components/Molecules/Chip/Chip"
import { Icon } from "@/components/Atoms/Icon"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer"
import { Button } from "@/components/ui/button"

export interface SubscriptionsByMemberFilterProps {
  searchQuery: string
  onSearchChange: (value: string) => void
  inscriptionFilter: string
  onInscriptionFilterChange: (value: string) => void
}

const INSCRIPTION_LABELS: Record<string, string> = {
  pendentes: "Pendentes",
}

export const SubscriptionsByMemberFilter: React.FC<SubscriptionsByMemberFilterProps> = ({
  searchQuery,
  onSearchChange,
  inscriptionFilter,
  onInscriptionFilterChange,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [draftInscription, setDraftInscription] = useState("pendentes")

  const hasActiveFilters = inscriptionFilter !== "todos"

  const openDrawer = () => {
    setDraftInscription(inscriptionFilter)
    setDrawerOpen(true)
  }

  const applyFilters = () => {
    onInscriptionFilterChange(draftInscription)
    setDrawerOpen(false)
  }

  const clearFilters = () => {
    onInscriptionFilterChange("todos")
    setDraftInscription("todos")
    setDrawerOpen(false)
  }

  const activeChips: { key: string; label: string; onRemove: () => void }[] = []

  if (inscriptionFilter !== "todos") {
    activeChips.push({
      key: "inscription",
      label: INSCRIPTION_LABELS[inscriptionFilter] ?? inscriptionFilter,
      onRemove: () => onInscriptionFilterChange("todos"),
    })
  }

  return (
    <div>
      {/* Mobile */}
      <div className="lg:hidden">
        <div className="flex items-center gap-3">
          <div className="relative flex flex-1 items-center gap-3 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Buscar por nome ou e-mail..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2.5 focus:outline-none"
            />
          </div>
          <button
            onClick={openDrawer}
            className="relative flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-black/40"
          >
            <i
              className={`fi fi-ts-sliders-v flex items-center justify-center text-base ${hasActiveFilters ? "text-orange-500" : "text-white"}`}
            />
            {hasActiveFilters && (
              <span className="absolute -right-1 -top-1 size-3 rounded-full bg-orange-500" />
            )}
          </button>
        </div>
        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onClose={chip.onRemove} />
            ))}
          </div>
        )}
      </div>

      {/* Desktop */}
      <div className="hidden lg:block">
        <div className="flex items-center gap-4">
          <div className="relative flex w-80 items-center gap-4 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Buscar por nome ou e-mail..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2 focus:outline-none"
            />
          </div>

          <Select onValueChange={onInscriptionFilterChange} value={inscriptionFilter}>
            <SelectTrigger
              className={`w-48 border-border bg-surface ${inscriptionFilter !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : ""}`}
            >
              <SelectValue placeholder="Todos os Status" />
            </SelectTrigger>
            <SelectContent className="border-border bg-surface">
              <SelectItem value="todos">Todos os Status</SelectItem>
              <SelectItem value="pendentes">Pendentes</SelectItem>
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="whitespace-nowrap text-sm text-muted-foreground transition-colors hover:text-orange-500"
            >
              Limpar filtros
            </button>
          )}
        </div>

        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onClose={chip.onRemove} />
            ))}
          </div>
        )}
      </div>

      {/* Mobile bottom sheet */}
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent className="border-white/10 bg-surface px-4 pb-2">
          <DrawerHeader>
            <DrawerTitle className="text-white">Filtros</DrawerTitle>
          </DrawerHeader>

          <div className="flex flex-col gap-4 px-4 pb-2">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Status</span>
              <Select onValueChange={setDraftInscription} value={draftInscription}>
                <SelectTrigger
                  className={`border-border bg-black/40 ${draftInscription !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : "text-white"}`}
                >
                  <SelectValue placeholder="Todos os Status" />
                </SelectTrigger>
                <SelectContent className="border-border bg-surface">
                  <SelectItem value="todos">Todos os Status</SelectItem>
                  <SelectItem value="pendentes">Pendentes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DrawerFooter>
            <Button onClick={applyFilters} className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600">
              Aplicar filtro
            </Button>
            <Button onClick={clearFilters} variant="ghost" className="w-full text-muted-foreground hover:text-white">
              Limpar filtro
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  )
}
